import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Table } from '@mantine/core'
import { ChevronDown } from 'lucide-react'
import type { StatusLogFiltersInput } from 'types/graphql'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { SegmentedTabs } from 'src/components/Core/SegmentedTabs'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_OUTCOMES } from 'src/graphql/pipelineMetrics.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import { formatSnakeValue } from 'src/lib/formatters'
import { cn } from 'src/utils'

import AnalyticsCard from '../../components/AnalyticsCard'
import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'

const INFO_TEXT =
  'Counts applicants whose application date falls within the selected date range, grouped by their CURRENT applicant stage and source. Totals match the Pipeline by Source card above. Click Not Interested or Disqualified to drill into per-reason breakdown. Conversion rates at the bottom = stage count ÷ total prospects, shown both per-source and overall.'

export type PipelineOutcomesViewMode = 'volume' | 'percent'

const VIEW_MODE_TABS = [
  { value: 'volume' as const, label: 'Volume' },
  { value: 'percent' as const, label: '%' },
] as const

/** Heatmap intensity bands keyed off `value / columnTotal`.
 *  Mirrors the playground's `h-empty / h-low / h-med / h-high / h-very`
 *  thresholds (5% / 15% / 25% / 40%). */
const heatmapClass = (value: number, columnTotal: number): string => {
  if (columnTotal === 0 || value === 0) return ''
  const pct = value / columnTotal
  if (pct >= 0.4)
    return 'bg-doubleNickel-brand-200 text-doubleNickel-gray-900 font-semibold'
  if (pct >= 0.25)
    return 'bg-doubleNickel-brand-100 text-doubleNickel-gray-900 font-semibold'
  if (pct >= 0.15) return 'bg-doubleNickel-brand-50 text-doubleNickel-gray-800'
  if (pct >= 0.05) return 'bg-doubleNickel-brand-25 text-doubleNickel-gray-700'
  return ''
}

/** Stage-row key as produced by `convertLeadConversionData`. */
type StageType =
  | 'LEAD'
  | 'HIRED'
  | 'NOT_INTERESTED'
  | 'DISQUALIFIED'
  | 'LEAD_TO_HIRED'
  | 'LEAD_TO_NOT_INTERESTED'
  | 'LEAD_TO_DISQUALIFIED'

interface ConversionRow {
  type: StageType
  TOTAL: number | string
  [sourceKey: string]: number | string
}

interface ReasonRow {
  TOTAL: number
  [sourceKey: string]: number | string | null
}

interface PipelineOutcomesShapedData {
  conversionRows: ConversionRow[]
  niReasons: ReasonRow[]
  dqReasons: ReasonRow[]
  niTotals: Record<string, number>
  dqTotals: Record<string, number>
  /** Stable, alphabetically-sorted list of source keys (link types) shown as columns. */
  sourceKeys: string[]
}

interface PipelineOutcomesTableViewProps {
  data: PipelineOutcomesShapedData | undefined
  loading: boolean
  /**
   * When provided, numeric cells in the stage rows and reason drill-down
   * rows become clickable, navigating to the matching Applicants slice.
   * Left optional so Storybook / tests can render without wiring a router.
   */
  filters?: StatusLogFiltersInput
}

/** Per-source stage rollup straight from `pipelineOutcomes.rows`. */
export interface PipelineOutcomesServerRow {
  source: string
  leadCount: number
  hiredCount: number
  notInterestedCount: number
  disqualifiedCount: number
}

/** Per (source, reason) row — used for the NI / DQ drill-down sections. */
export interface PipelineOutcomesServerReasonRow {
  source: string
  reason: string
  count: number
}

/** Cap a percentage and format with one decimal — matches the legacy
 *  presentation for the conversion-rate rows so the visual stays the same. */
const formatRate = (count: number, total: number): string => {
  if (total <= 0) return '0.0%'
  const raw = (count / total) * 100
  const capped = Math.min(raw, 100)
  return `${capped.toFixed(1)}%`
}

/**
 * Pivot the new pipelineOutcomes server response into the table-ready shape
 * the renderer expects. Pure — no React. Keeps the renderer untouched.
 *
 *   Per-source stage rows:    rows         → conversionRows (LEAD / HIRED / NI / DQ + 3 rate rows)
 *   Per (source, NI-reason):  niReasons    → niReasons + niTotals
 *   Per (source, DQ-reason):  dqReasons    → dqReasons + dqTotals
 */
export const shapePipelineOutcomesData = (
  rows: PipelineOutcomesServerRow[],
  notInterestedReasons: PipelineOutcomesServerReasonRow[],
  disqualifiedReasons: PipelineOutcomesServerReasonRow[]
): PipelineOutcomesShapedData => {
  // Stable, alphabetically-sorted source keys derived from the server result.
  // We include any source that shows up in `rows`, even if its leadCount is 0
  // (it could still have hires/NI/DQ for the period — and the renderer treats
  // the prospect row as the column total, so a 0-prospect column is valid).
  const sourceKeys = Array.from(new Set(rows.map((r) => r.source))).sort()

  // ---- Stage rows (LEAD / HIRED / NOT_INTERESTED / DISQUALIFIED + 3 rates) ----
  const lookupRow = (source: string) => rows.find((r) => r.source === source)

  const stageCount = (
    source: string,
    field: keyof PipelineOutcomesServerRow
  ): number => {
    const row = lookupRow(source)
    if (!row) return 0
    const v = row[field]
    return typeof v === 'number' ? v : 0
  }

  const sumStage = (field: keyof PipelineOutcomesServerRow): number =>
    sourceKeys.reduce((acc, k) => acc + stageCount(k, field), 0)

  const buildStageRow = (
    type: StageType,
    field: keyof PipelineOutcomesServerRow
  ): ConversionRow => {
    const row: ConversionRow = { type, TOTAL: 0 }
    for (const key of sourceKeys) row[key] = stageCount(key, field)
    row.TOTAL = sumStage(field)
    return row
  }

  const leadRow = buildStageRow('LEAD', 'leadCount')
  const hiredRow = buildStageRow('HIRED', 'hiredCount')
  const niRow = buildStageRow('NOT_INTERESTED', 'notInterestedCount')
  const dqRow = buildStageRow('DISQUALIFIED', 'disqualifiedCount')

  const buildRateRow = (
    type: StageType,
    numeratorRow: ConversionRow
  ): ConversionRow => {
    const row: ConversionRow = { type, TOTAL: 0 }
    for (const key of sourceKeys) {
      const n =
        typeof numeratorRow[key] === 'number'
          ? (numeratorRow[key] as number)
          : 0
      const d = typeof leadRow[key] === 'number' ? (leadRow[key] as number) : 0
      row[key] = formatRate(n, d)
    }
    const numTotal =
      typeof numeratorRow.TOTAL === 'number' ? numeratorRow.TOTAL : 0
    const denomTotal = typeof leadRow.TOTAL === 'number' ? leadRow.TOTAL : 0
    row.TOTAL = formatRate(numTotal, denomTotal)
    return row
  }

  const conversionRows: ConversionRow[] = [
    leadRow,
    hiredRow,
    niRow,
    dqRow,
    buildRateRow('LEAD_TO_HIRED', hiredRow),
    buildRateRow('LEAD_TO_NOT_INTERESTED', niRow),
    buildRateRow('LEAD_TO_DISQUALIFIED', dqRow),
  ]

  // ---- Reason rollups ---------------------------------------------------
  // The renderer expects `{ NOT_INTERESTED_REASON: <reason>, [sourceKey]: count, TOTAL: count }`
  // (and the DQ equivalent). We pivot the flat (source, reason, count) list
  // into that wide format below. Reasons are alphabetically sorted so the
  // drill-down stays stable across renders.
  const buildReasonRows = (
    flat: PipelineOutcomesServerReasonRow[],
    reasonKey: 'NOT_INTERESTED_REASON' | 'DISQUALIFIED_REASON'
  ): { rows: ReasonRow[]; totals: Record<string, number> } => {
    const reasons = Array.from(new Set(flat.map((r) => r.reason))).sort()
    const totals: Record<string, number> = {}

    const out: ReasonRow[] = reasons.map((reason) => {
      const row: ReasonRow = { TOTAL: 0, [reasonKey]: reason }
      for (const key of sourceKeys) row[key] = 0
      let total = 0
      for (const item of flat) {
        if (item.reason !== reason) continue
        // Only roll up reasons whose source is known to the table — keeps the
        // column set deterministic and avoids ghost columns.
        if (!sourceKeys.includes(item.source)) continue
        row[item.source] = item.count
        total += item.count
        totals[item.source] = (totals[item.source] ?? 0) + item.count
      }
      row.TOTAL = total
      return row
    })

    return { rows: out, totals }
  }

  const ni = buildReasonRows(notInterestedReasons, 'NOT_INTERESTED_REASON')
  const dq = buildReasonRows(disqualifiedReasons, 'DISQUALIFIED_REASON')

  return {
    conversionRows,
    niReasons: ni.rows,
    dqReasons: dq.rows,
    niTotals: ni.totals,
    dqTotals: dq.totals,
    sourceKeys,
  }
}

const formatSourceLabel = (key: string): string =>
  LinkTypeDisplay[key as keyof typeof LinkTypeDisplay] ?? formatSnakeValue(key)

/** Outcome label slot — fixed-width chevron slot ensures Hired and the
 *  expandable rows share the same horizontal label baseline. */
const OutcomeLabel = ({
  dotClass,
  label,
  expandable,
  expanded,
}: {
  dotClass: string
  label: string
  expandable: boolean
  expanded?: boolean
}) => (
  <span className="inline-flex items-center gap-2">
    <span
      aria-hidden="true"
      className="inline-flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center text-doubleNickel-gray-400"
    >
      {expandable && (
        <ChevronDown
          size={10}
          strokeWidth={2}
          className={cn('transition-transform', !expanded && '-rotate-90')}
        />
      )}
    </span>
    <span
      aria-hidden="true"
      className={cn('h-2 w-2 flex-shrink-0 rounded-full', dotClass)}
    />
    <span>{label}</span>
  </span>
)

/** Format a numeric cell in the active view-mode (volume or percent of column). */
const formatNumericCell = (
  value: number | string | null | undefined,
  columnTotal: number,
  viewMode: PipelineOutcomesViewMode
): string => {
  if (value == null) return '—'
  // The conversion-rate rows pre-format their values as strings (e.g. "7.5%").
  if (typeof value === 'string') return value
  if (viewMode === 'percent') {
    if (columnTotal === 0) return '0%'
    return `${((value / columnTotal) * 100).toFixed(0)}%`
  }
  return value.toLocaleString()
}

const STICKY_STAGE_TH =
  'sticky left-0 z-20 bg-doubleNickel-gray-50 text-left text-xs font-semibold text-doubleNickel-gray-500 px-3 py-2.5 w-[200px] min-w-[200px]'
const STICKY_TOTAL_TH =
  'sticky left-[200px] z-20 bg-doubleNickel-gray-50 text-center text-xs font-bold text-doubleNickel-gray-700 px-3 py-2.5 w-[88px] min-w-[88px] border-r border-gray-200'
const STICKY_STAGE_TD =
  'sticky left-0 z-10 bg-white text-left text-xs px-3 py-2 w-[200px] min-w-[200px]'
const STICKY_TOTAL_TD =
  'sticky left-[200px] z-10 bg-white text-center text-xs font-semibold text-doubleNickel-gray-800 px-3 py-2 w-[88px] min-w-[88px] border-r border-gray-200'

/**
 * Presentational table — pure function of pre-shaped data. The Apollo wrapper
 * below handles fetching. Splitting this way keeps tests + Storybook trivial.
 */
export const PipelineOutcomesTableView = ({
  data,
  loading,
  filters,
}: PipelineOutcomesTableViewProps) => {
  const [viewMode, setViewMode] = useState<PipelineOutcomesViewMode>('volume')
  const [outcomesOpen, setOutcomesOpen] = useState(true)
  const [niOpen, setNiOpen] = useState(false)
  const [dqOpen, setDqOpen] = useState(false)

  // Click-through builders. Two modes:
  //
  // Top-level Hired / NI / DQ rows use `cohort-reached` mode: the resolver
  // counts applicants whose applicationDate ∈ window AND whose <stage>_date
  // IS NOT NULL (ever-reached-stage), and the click hits the same set.
  // current-stage would drift on hires that later got terminated (or
  // re-entered PROSPECT). See buildApplicantsFilterUrl's `cohort-reached`
  // mode docs.
  //
  // Reason sub-rows are intentionally non-clickable: the Applicants page's
  // reason filter keys off current stage (`applicantStages`), not the
  // cohort, so a click would land on a superset that didn't match the
  // displayed count. Reinstate once the URL builder supports a
  // cohort-scoped reason filter.
  const clickable = Boolean(filters?.dateRange)
  const navigateToStage = (stage: ApplicantStage, sourceKey: string | null) => {
    if (!filters?.dateRange) return
    // sourceKey === null is the TOTAL column: no per-source override, but
    // page-level linkTypes still pass through. A specific source overrides.
    const leadSources =
      sourceKey === null
        ? (filters.linkTypes as string[] | null | undefined) ?? undefined
        : [sourceKey]
    navigate(
      buildApplicantsFilterUrl({
        mode: 'cohort-reached',
        stage,
        dateRange: filters.dateRange,
        recruiterIds: filters.recruiters ?? undefined,
        jobListingIds: filters.jobListings ?? undefined,
        leadSources,
      })
    )
  }
  const clickableCellClass = clickable ? 'cursor-pointer hover:underline' : ''

  const headerChildren = (
    <SegmentedTabs
      tabs={VIEW_MODE_TABS}
      value={viewMode}
      onChange={setViewMode}
      ariaLabel="Pipeline outcomes view mode"
    />
  )

  // Loading / empty go through the AnalyticsCard's own slots so they share
  // skeleton + "no data" UX with the rest of the analytics page.
  if (loading) {
    return (
      <AnalyticsCard
        title="Pipeline Outcomes"
        info={INFO_TEXT}
        headerChildren={headerChildren}
        loading
      >
        <div />
      </AnalyticsCard>
    )
  }

  // `convertLeadConversionData` always produces 7 stage rows even for empty
  // input — so emptiness is determined by whether any source columns showed up.
  const isEmpty = !data || data.sourceKeys.length === 0
  if (isEmpty) {
    return (
      <AnalyticsCard
        title="Pipeline Outcomes"
        info={INFO_TEXT}
        headerChildren={headerChildren}
      >
        <Empty
          title="No data available"
          subtitle="There is no data available for the selected date range."
        />
      </AnalyticsCard>
    )
  }

  const {
    conversionRows,
    niReasons,
    dqReasons,
    niTotals,
    dqTotals,
    sourceKeys,
  } = data

  const findRow = (type: StageType) =>
    conversionRows.find((r) => r.type === type)

  const prospectRow = findRow('LEAD')
  const hiredRow = findRow('HIRED')
  const notInterestedRow = findRow('NOT_INTERESTED')
  const disqualifiedRow = findRow('DISQUALIFIED')
  const hiredRateRow = findRow('LEAD_TO_HIRED')
  const niRateRow = findRow('LEAD_TO_NOT_INTERESTED')
  const dqRateRow = findRow('LEAD_TO_DISQUALIFIED')

  const colspanFull = sourceKeys.length + 2

  const numericValue = (
    row: ConversionRow | undefined,
    key: string
  ): number => {
    if (!row) return 0
    const v = row[key]
    return typeof v === 'number' ? v : 0
  }

  // For the outcome-row cells we treat the prospect row as the column total —
  // the same definition the bottom rate-rows use, keeping the % view internally
  // consistent.
  const prospectColumnTotal = (key: string): number =>
    numericValue(prospectRow, key)

  const renderOutcomeCell = (
    row: ConversionRow | undefined,
    key: string
  ): string => {
    if (!row) return '—'
    return formatNumericCell(row[key], prospectColumnTotal(key), viewMode)
  }

  return (
    <AnalyticsCard
      title="Pipeline Outcomes"
      info={INFO_TEXT}
      headerChildren={headerChildren}
    >
      <div className="overflow-x-auto" data-testid="pipeline-outcomes-scroller">
        <Table
          classNames={{ tbody: 'text-nowrap' }}
          data-testid="pipeline-outcomes-table"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={STICKY_STAGE_TH}>Stage</Table.Th>
              <Table.Th className={STICKY_TOTAL_TH}>Total</Table.Th>
              {sourceKeys.map((key) => (
                <Table.Th
                  key={key}
                  className="bg-doubleNickel-gray-50 px-3 py-2.5 text-center text-xs font-semibold text-doubleNickel-gray-500"
                >
                  {formatSourceLabel(key)}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* === Prospect (top of funnel) === */}
            {prospectRow && (
              <Table.Tr
                className="bg-doubleNickel-blue-50"
                data-testid="row-prospect"
              >
                <Table.Td
                  className={cn(
                    STICKY_STAGE_TD,
                    'bg-doubleNickel-blue-50 font-bold text-doubleNickel-gray-900'
                  )}
                >
                  Prospect
                </Table.Td>
                <Table.Td
                  className={cn(
                    STICKY_TOTAL_TD,
                    'bg-doubleNickel-blue-50 font-bold text-doubleNickel-gray-900',
                    clickableCellClass
                  )}
                  onClick={
                    clickable
                      ? () => navigateToStage(ApplicantStage.PROSPECT, null)
                      : undefined
                  }
                >
                  {typeof prospectRow.TOTAL === 'number'
                    ? prospectRow.TOTAL.toLocaleString()
                    : prospectRow.TOTAL}
                </Table.Td>
                {sourceKeys.map((key) => (
                  <Table.Td
                    key={key}
                    className={cn(
                      'bg-doubleNickel-blue-50 px-3 py-2 text-center text-xs font-semibold text-doubleNickel-gray-800',
                      clickableCellClass
                    )}
                    onClick={
                      clickable
                        ? () => navigateToStage(ApplicantStage.PROSPECT, key)
                        : undefined
                    }
                  >
                    {numericValue(prospectRow, key).toLocaleString()}
                  </Table.Td>
                ))}
              </Table.Tr>
            )}

            {/* === Outcomes section header (collapses entire outcomes block) === */}
            <Table.Tr
              className="cursor-pointer bg-doubleNickel-gray-50 hover:bg-gray-100"
              onClick={() => setOutcomesOpen((o) => !o)}
              data-testid="outcomes-section-toggle"
            >
              <Table.Td
                colSpan={colspanFull}
                className="sticky left-0 z-10 bg-doubleNickel-gray-50 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-doubleNickel-gray-500 hover:bg-gray-100"
              >
                <span className="inline-flex items-center gap-1.5">
                  <ChevronDown
                    size={12}
                    className={cn(
                      'text-doubleNickel-gray-400 transition-transform',
                      !outcomesOpen && '-rotate-90'
                    )}
                  />
                  Outcomes
                </span>
              </Table.Td>
            </Table.Tr>

            {outcomesOpen && (
              <>
                {/* === Hired row === */}
                {hiredRow && (
                  <Table.Tr data-testid="row-hired">
                    <Table.Td className={STICKY_STAGE_TD}>
                      <OutcomeLabel
                        dotClass="bg-doubleNickel-success-600"
                        label="Hired"
                        expandable={false}
                      />
                    </Table.Td>
                    <Table.Td
                      className={cn(STICKY_TOTAL_TD, clickableCellClass)}
                      onClick={
                        clickable
                          ? () => navigateToStage(ApplicantStage.HIRED, null)
                          : undefined
                      }
                    >
                      {formatNumericCell(
                        hiredRow.TOTAL,
                        numericValue(prospectRow, 'TOTAL'),
                        viewMode
                      )}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className={cn(
                          'px-3 py-2 text-center text-xs text-doubleNickel-gray-700',
                          clickableCellClass
                        )}
                        onClick={
                          clickable
                            ? () => navigateToStage(ApplicantStage.HIRED, key)
                            : undefined
                        }
                      >
                        {renderOutcomeCell(hiredRow, key)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}

                {/* === Not Interested (expandable) === */}
                {notInterestedRow && (
                  <Table.Tr
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setNiOpen((o) => !o)}
                    data-testid="row-not-interested"
                  >
                    <Table.Td className={STICKY_STAGE_TD}>
                      <OutcomeLabel
                        dotClass="bg-doubleNickel-gray-500"
                        label="Not Interested"
                        expandable
                        expanded={niOpen}
                      />
                    </Table.Td>
                    <Table.Td
                      className={cn(STICKY_TOTAL_TD, clickableCellClass)}
                      onClick={
                        clickable
                          ? (e) => {
                              // Stop the parent <Tr> from also toggling
                              // the NI section open/closed when the cell
                              // initiates a route change.
                              e.stopPropagation()
                              navigateToStage(
                                ApplicantStage.NOT_INTERESTED,
                                null
                              )
                            }
                          : undefined
                      }
                    >
                      {formatNumericCell(
                        notInterestedRow.TOTAL,
                        numericValue(prospectRow, 'TOTAL'),
                        viewMode
                      )}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className={cn(
                          'px-3 py-2 text-center text-xs text-doubleNickel-gray-700',
                          clickableCellClass
                        )}
                        onClick={
                          clickable
                            ? (e) => {
                                e.stopPropagation()
                                navigateToStage(
                                  ApplicantStage.NOT_INTERESTED,
                                  key
                                )
                              }
                            : undefined
                        }
                      >
                        {renderOutcomeCell(notInterestedRow, key)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}

                {niOpen &&
                  niReasons.map((row, idx) => {
                    const reasonStr = String(row['NOT_INTERESTED_REASON'] ?? '')
                    return (
                      <Table.Tr
                        key={`ni-${idx}`}
                        data-testid="row-ni-reason"
                        className="bg-[#fafbfc]"
                      >
                        <Table.Td
                          className={cn(
                            STICKY_STAGE_TD,
                            'bg-[#fafbfc] pl-[54px] text-doubleNickel-gray-700'
                          )}
                        >
                          {formatSnakeValue(reasonStr)}
                        </Table.Td>
                        <Table.Td
                          className={cn(
                            STICKY_TOTAL_TD,
                            'bg-[#fafbfc] text-doubleNickel-gray-800'
                          )}
                        >
                          {(row.TOTAL ?? 0).toLocaleString()}
                        </Table.Td>
                        {sourceKeys.map((key) => {
                          const raw = row[key]
                          const value = typeof raw === 'number' ? raw : 0
                          const columnTotal = niTotals[key] ?? 0
                          return (
                            <Table.Td
                              key={key}
                              className={cn(
                                'px-3 py-1.5 text-center text-xs',
                                heatmapClass(value, columnTotal)
                              )}
                            >
                              {value === 0
                                ? '—'
                                : formatNumericCell(
                                    value,
                                    columnTotal,
                                    viewMode
                                  )}
                            </Table.Td>
                          )
                        })}
                      </Table.Tr>
                    )
                  })}

                {/* === Disqualified (expandable) === */}
                {disqualifiedRow && (
                  <Table.Tr
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setDqOpen((o) => !o)}
                    data-testid="row-disqualified"
                  >
                    <Table.Td className={STICKY_STAGE_TD}>
                      <OutcomeLabel
                        dotClass="bg-doubleNickel-error-600"
                        label="Disqualified"
                        expandable
                        expanded={dqOpen}
                      />
                    </Table.Td>
                    <Table.Td
                      className={cn(STICKY_TOTAL_TD, clickableCellClass)}
                      onClick={
                        clickable
                          ? (e) => {
                              e.stopPropagation()
                              navigateToStage(ApplicantStage.DISQUALIFIED, null)
                            }
                          : undefined
                      }
                    >
                      {formatNumericCell(
                        disqualifiedRow.TOTAL,
                        numericValue(prospectRow, 'TOTAL'),
                        viewMode
                      )}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className={cn(
                          'px-3 py-2 text-center text-xs text-doubleNickel-gray-700',
                          clickableCellClass
                        )}
                        onClick={
                          clickable
                            ? (e) => {
                                e.stopPropagation()
                                navigateToStage(
                                  ApplicantStage.DISQUALIFIED,
                                  key
                                )
                              }
                            : undefined
                        }
                      >
                        {renderOutcomeCell(disqualifiedRow, key)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}

                {dqOpen &&
                  dqReasons.map((row, idx) => {
                    const reasonStr = String(row['DISQUALIFIED_REASON'] ?? '')
                    return (
                      <Table.Tr
                        key={`dq-${idx}`}
                        data-testid="row-dq-reason"
                        className="bg-[#fafbfc]"
                      >
                        <Table.Td
                          className={cn(
                            STICKY_STAGE_TD,
                            'bg-[#fafbfc] pl-[54px] text-doubleNickel-gray-700'
                          )}
                        >
                          {formatSnakeValue(reasonStr)}
                        </Table.Td>
                        <Table.Td
                          className={cn(
                            STICKY_TOTAL_TD,
                            'bg-[#fafbfc] text-doubleNickel-gray-800'
                          )}
                        >
                          {(row.TOTAL ?? 0).toLocaleString()}
                        </Table.Td>
                        {sourceKeys.map((key) => {
                          const raw = row[key]
                          const value = typeof raw === 'number' ? raw : 0
                          const columnTotal = dqTotals[key] ?? 0
                          return (
                            <Table.Td
                              key={key}
                              className={cn(
                                'px-3 py-1.5 text-center text-xs',
                                heatmapClass(value, columnTotal)
                              )}
                            >
                              {value === 0
                                ? '—'
                                : formatNumericCell(
                                    value,
                                    columnTotal,
                                    viewMode
                                  )}
                            </Table.Td>
                          )
                        })}
                      </Table.Tr>
                    )
                  })}

                {/* === Conversion-rate rows (italic) === */}
                {hiredRateRow && (
                  <Table.Tr
                    data-testid="row-hired-rate"
                    className="border-t border-gray-100"
                  >
                    <Table.Td
                      className={cn(
                        STICKY_STAGE_TD,
                        'italic text-doubleNickel-gray-400'
                      )}
                    >
                      → Hired Rate
                    </Table.Td>
                    <Table.Td
                      className={cn(
                        STICKY_TOTAL_TD,
                        'font-semibold not-italic text-doubleNickel-gray-500'
                      )}
                    >
                      {hiredRateRow.TOTAL}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className="px-3 py-1.5 text-center text-xs italic text-doubleNickel-gray-400"
                      >
                        {hiredRateRow[key]}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}

                {niRateRow && (
                  <Table.Tr data-testid="row-ni-rate">
                    <Table.Td
                      className={cn(
                        STICKY_STAGE_TD,
                        'italic text-doubleNickel-gray-400'
                      )}
                    >
                      → Not Interested Rate
                    </Table.Td>
                    <Table.Td
                      className={cn(
                        STICKY_TOTAL_TD,
                        'font-semibold not-italic text-doubleNickel-gray-500'
                      )}
                    >
                      {niRateRow.TOTAL}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className="px-3 py-1.5 text-center text-xs italic text-doubleNickel-gray-400"
                      >
                        {niRateRow[key]}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}

                {dqRateRow && (
                  <Table.Tr data-testid="row-dq-rate">
                    <Table.Td
                      className={cn(
                        STICKY_STAGE_TD,
                        'italic text-doubleNickel-gray-400'
                      )}
                    >
                      → Disqualified Rate
                    </Table.Td>
                    <Table.Td
                      className={cn(
                        STICKY_TOTAL_TD,
                        'font-semibold not-italic text-doubleNickel-gray-500'
                      )}
                    >
                      {dqRateRow.TOTAL}
                    </Table.Td>
                    {sourceKeys.map((key) => (
                      <Table.Td
                        key={key}
                        className="px-3 py-1.5 text-center text-xs italic text-doubleNickel-gray-400"
                      >
                        {dqRateRow[key]}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </div>
    </AnalyticsCard>
  )
}

interface PipelineOutcomesTableProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
}

/**
 * Apollo-bound wrapper. Single query against pipelineOutcomes — same join
 * chain + filters as pipelineSourceBreakdown above, so totals on the
 * Pipeline by Source card and this card agree by construction (both query
 * the Applicant table, not StatusLog). Forwards every facet the page-level
 * filters expose so the two cards never drift when the Marketing tab adds
 * recruiter / job-listing / link-type filtering.
 */
const PipelineOutcomesTable = ({
  filters,
  refreshKey,
}: PipelineOutcomesTableProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_PIPELINE_OUTCOMES, {
    variables: {
      input: {
        filters: {
          dateRange: filters.dateRange,
          recruiters: filters.recruiters,
          jobListings: filters.jobListings,
          linkTypes: filters.linkTypes,
          jobTypes: filters.jobTypes,
        },
      },
    },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  // Skip the initial mount — the useQuery above already kicked off the first
  // fetch. Only call refetch on subsequent refreshKey changes.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const shaped = useMemo(() => {
    const result = data?.pipelineOutcomes
    if (!result) return undefined
    return shapePipelineOutcomesData(
      result.rows as PipelineOutcomesServerRow[],
      result.notInterestedReasons as PipelineOutcomesServerReasonRow[],
      result.disqualifiedReasons as PipelineOutcomesServerReasonRow[]
    )
  }, [data])

  return (
    <PipelineOutcomesTableView
      data={shaped}
      loading={loading}
      filters={filters}
    />
  )
}

export default PipelineOutcomesTable
